import { useUpdateCompany } from '@api/settings';
import { ActionsFooter, CheckboxField } from '@components/Forms';
import ApprovalDialogButton from '@components/Forms/ApprovalDialog/ApprovalDialogButton';
import SettingsSection from '@components/ProjectSettings/SettingsSection';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { AnalyticsActionName } from '@utils/analytics';
import { onSubmitForm } from '@utils/forms';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import useAnalytics from 'hooks/useAnalytics';
import { AuthContext } from 'hooks/useAuth';
import React, { FC, useContext } from 'react';
import { ICompany } from 'types/settings';
import { object, string } from 'yup';
import CompanySettingsSSOSection from './CompanySettingsSSOSection';

const FORM_KEYS = {
	id: 'id',
	name: 'name',
	supportConcent: 'supportConcent'
};
const DEFAULT_FORM: ICompany = {
	[FORM_KEYS.id]: '',
	[FORM_KEYS.name]: '',
	[FORM_KEYS.supportConcent]: false
} as unknown as ICompany;

const CompanySettings: FC<{ data: ICompany }> = ({ data }) => {
	const { checkLogin } = useContext(AuthContext);
	const { addToast } = useToast();
	const updateSettings = useUpdateCompany();
	const track = useAnalytics();

	const initialValues = React.useMemo(
		() => ({
			...DEFAULT_FORM,
			...data
		}),
		[data]
	);

	const formik = useFormik<ICompany>({
		enableReinitialize: true,
		initialValues,
		validationSchema: object({
			[FORM_KEYS.name]: string()
				.test(
					'len',
					intl.tr('company.fields.validation.company.name'),
					(value) => (value || '').length >= 3
				)
				.required(intl.tr('company.fields.validation.company.name.required'))
		}),
		onSubmit: onSubmitForm<ICompany>(async (newData) => {
			const res = await updateSettings.mutateAsync(newData);
			track(AnalyticsActionName.companySettings, res);

			if (newData.name !== initialValues?.name) {
				checkLogin();
			}
			return res;
		}, addToast)
	});

	return (
		<Box
			display="flex"
			flexGrow={1}
			flexDirection="column"
			id="company-settings-container"
			overflow="auto"
			component="form"
		>
			<Box flexGrow={1} pb={2} flexDirection="column" overflow="auto">
				<Grid container spacing={3} pt={1}>
					<Grid item xs={12} sm={5}>
						<TextFieldValidation
							id={FORM_KEYS.name}
							formik={formik}
							fullWidth
							required
							label={intl.tr(`company.${FORM_KEYS.name}`)}
						/>
					</Grid>
					<Grid item xs={12} sm={5}>
						<TextFieldValidation
							id={FORM_KEYS.id}
							formik={formik}
							copy
							label={intl.tr(`company.${FORM_KEYS.id}`)}
							disabled
							fullWidth
						/>
					</Grid>
				</Grid>
				<CompanySettingsSSOSection currentCompany={data.id} />
				<SettingsSection title={intl.tr('company.support.header')}>
					<Grid container>
						<Grid item xs={12}>
							<CheckboxField
								id={FORM_KEYS.supportConcent}
								formik={formik}
								textLabel={intl.tr('company.supportConcent')}
							/>
						</Grid>
					</Grid>
				</SettingsSection>
			</Box>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty}
					sx={{ marginRight: 1 }}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
			</ActionsFooter>
		</Box>
	);
};

export default CompanySettings;
